import { inject, Injectable, Signal } from '@angular/core';
import { ComputersHealthFilterType } from 'mbs-ui-shared@models/Computer';
import {
  RmmComputersStatistic,
  RmmPageFields,
  RmmPageFilterItemField,
  RmmPageGetDataQueryParams,
  RmmPageQuery,
  RmmPageQueryPayload,
  RmmPageTemplate,
  RmmViewColumnData
} from 'mbs-ui-shared@models/rmm-warehouse.models';
import { RmmWarehouseStore } from 'mbs-ui-shared@store/rmmWarehouse/rmmWarehouseStore';

@Injectable({
  providedIn: 'root'
})
export class RmmWarehouseFacade {
  private store = inject(RmmWarehouseStore);

  constructor() {
    this.loadPresets();
  }

  get loading(): Signal<boolean> {
    return this.store.loading;
  }

  get dataLoading(): Signal<boolean> {
    return this.store.dataLoading;
  }

  get presets(): Signal<RmmPageTemplate[]> {
    return this.store.presets;
  }

  get preset(): Signal<RmmPageTemplate> {
    return this.store.preset;
  }

  get params(): Signal<RmmPageGetDataQueryParams> {
    return this.store.params;
  }

  get query(): Signal<RmmPageQuery> {
    return this.store.query;
  }

  get presetId(): Signal<string> {
    return this.store.presetId;
  }

  get page(): Signal<number> {
    return this.store.page;
  }

  get pageSize(): Signal<number> {
    return this.store.pageSize;
  }

  get data(): Signal<any[]> {
    return this.store.data;
  }

  get total(): Signal<number> {
    return this.store.total;
  }

  get statistic(): Signal<RmmComputersStatistic | null> {
    return this.store.statistic;
  }

  get statisticLoading(): Signal<boolean> {
    return this.store.statisticLoading;
  }

  get searchConditions(): Signal<RmmPageFilterItemField[]> {
    return this.store.searchConditions;
  }

  get statisticFilter(): Signal<ComputersHealthFilterType> {
    return this.store.statisticFilter;
  }

  get searchTotal(): Signal<number | null> {
    return this.store.searchTotal;
  }

  get sortableFields(): Signal<RmmPageFields[]> {
    return this.store.sortableFields;
  }

  setFilters(searchConditions: RmmPageFilterItemField[], statisticFilter: ComputersHealthFilterType): void {
    this.store.setFilters(searchConditions, statisticFilter);
  }

  loadFields(): void {
    this.store.loadFields();
  }

  loadData(): void {
    this.store.loadData({ presetId: this.store.presetId() });
  }

  loadStatistic(): void {
    this.store.loadStatistic();
  }

  refresh(): void {
    this.loadStatistic();
    this.loadData();
  }

  updateQuery(query: Partial<RmmPageQuery>, withLoad = false): void {
    this.store.updateQuery(query);

    if (withLoad) this.loadData();
  }

  updateUIState(uiState: RmmViewColumnData[]): void {
    this.store.updateUIState(uiState);
  }

  clearPreset(): void {
    this.store.clearPreset();
  }

  updatePaging(page: number, pageSize: number, withLoad = false): void {
    this.store.updatePaging(page, pageSize);
    if (withLoad) this.loadData();
  }

  loadPresets(): void {
    this.store.loadPresets({});
  }

  setPresetById(presetId: string): void {
    if (presetId === this.store.presetId()) return;

    const preset = this.store.presets().find((item) => item.id === presetId);

    if (preset) {
      this.store.changePreset(preset);
      this.loadData();
      return;
    }

    this.store.loadPresets({ presetId });
  }

  savePreset(payload: RmmPageQueryPayload): void {
    this.store.savePreset({ payload });
  }

  updatePreset(id: string, payload: RmmPageQueryPayload): void {
    this.store.updatePreset({ id, payload });
  }

  removePreset(id: string): void {
    this.store.removePreset({ id });
  }
}
